import { UNASSIGNED_QHP_ID } from '@/constants/visit';
import { OrderStateFilter, orderStatesToOrderStateFilter } from '@/types/order';
import { VisitOutcome, VisitPriority, visitOutcomeFromZodCode } from '@/types/visit';
import { atomWithSessionStorage } from '@/utils/browserStorage';
import { Objects } from '@/utils/objects';
import { VisitFilter, VisitRequestFilter } from 'gql-types';
import { useAtomValue } from 'jotai';
import xor from 'lodash/xor';
import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

export const visitFiltersAtom = atomWithSessionStorage(
    'visitFilters',
    {
        // The keys of this object are exactly the keys sent to the backend and
        //  parsed by it, so don't change them.
        patient_tag_ids: [],
        qhps: [],
        orgs: [],
        territories: [],
        services: [],
        outcomes: [],
        qhp_teams_uuids: [],
        qualifications_uuids: [],
        patients: [],
        service_areas: [],
        priority_codes: [],
        discipline_uuids: [],
        orderState: [],
    },
    z.object({
        patient_tag_ids: z.array(z.string()),
        qhps: z.array(z.string()),
        orgs: z.array(z.string()),
        territories: z.array(z.string()),
        services: z.array(z.string()),
        outcomes: z.array(z.string()),
        qhp_teams_uuids: z.array(z.string()),
        qualifications_uuids: z.array(z.string()),
        patients: z.array(z.string()),
        service_areas: z.array(z.string()),
        priority_codes: z.array(z.string()),
        discipline_uuids: z.array(z.string()),
        orderState: z.optional(z.array(z.nativeEnum(OrderStateFilter)).nullable()),
    }),
);

export const useVisitFilterDialogFilters = (): VisitFilter => {
    const filterState = useAtomValue(visitFiltersAtom);
    return useMemo((): VisitFilter => {
        const filters: VisitFilter = {};
        const assignedQhps = filterState.qhps.filter((id) => id !== UNASSIGNED_QHP_ID);
        const includeUnassigned = filterState.qhps.includes(UNASSIGNED_QHP_ID);
        if (includeUnassigned && assignedQhps.length) {
            filters.OR = {
                needsClinicians: { exact: true },
                assignedClinicians: { id: { inList: assignedQhps } },
            };
        } else if (includeUnassigned) {
            filters.needsClinicians = { exact: true };
        } else if (assignedQhps.length) {
            filters.assignedClinicians = { id: { inList: assignedQhps } };
        }
        if (filterState.qhp_teams_uuids.length) {
            filters.assignedClinicianTeams = { id: { inList: filterState.qhp_teams_uuids } };
        }
        if (filterState.orgs.length) {
            filters.organization = { id: { inList: filterState.orgs } };
        }
        if (filterState.territories.length) {
            filters.territory = { id: { inList: filterState.territories } };
        }
        if (filterState.services.length) {
            filters.services = { id: { inList: filterState.services } };
        }
        if (filterState.outcomes.length) {
            filters.outcome = { inList: filterState.outcomes.map(visitOutcomeFromZodCode) };
        }
        if (filterState.qualifications_uuids.length) {
            filters.qualifications = { id: { inList: filterState.qualifications_uuids } };
        }
        if (filterState.patients.length) {
            filters.patient = { id: { inList: filterState.patients } };
        }
        if (filterState.patient_tag_ids.length) {
            filters.patient = {
                ...(filters.patient ?? {}),
                hasTag: { id: { inList: filterState.patient_tag_ids } },
            };
        }
        if (filterState.service_areas.length) {
            filters.serviceArea = { id: { inList: filterState.service_areas } };
        }
        if (filterState.priority_codes.length) {
            filters.priority = { inList: filterState.priority_codes as VisitPriority[] };
        }
        if (filterState.discipline_uuids.length) {
            filters.disciplines = { id: { inList: filterState.discipline_uuids } };
        }
        if (filterState.orderState?.length) {
            filters.relatedOrder = orderStatesToOrderStateFilter(filterState.orderState);
        }
        return filters;
    }, [filterState]);
};

export const useVisitRequestFilterDialogFilters = (): VisitRequestFilter => {
    const filterState = useAtomValue(visitFiltersAtom);
    return useMemo((): VisitRequestFilter => {
        const hasNonNoneOutcome = filterState.outcomes.some((o) => o !== VisitOutcome.NONE);
        if (hasNonNoneOutcome) {
            return { id: { inList: [] } };
        }
        const filters: VisitRequestFilter = {};
        const assignedQhps = filterState.qhps.filter((id) => id !== UNASSIGNED_QHP_ID);
        const includeUnassigned = filterState.qhps.includes(UNASSIGNED_QHP_ID);
        if (includeUnassigned && assignedQhps.length) {
            filters.OR = {
                needsClinicians: { exact: true },
                assignedClinicians: { id: { inList: assignedQhps } },
            };
        } else if (includeUnassigned) {
            filters.needsClinicians = { exact: true };
        } else if (assignedQhps.length) {
            filters.assignedClinicians = { id: { inList: assignedQhps } };
        }
        if (filterState.qhp_teams_uuids.length) {
            filters.assignedClinicianTeams = { id: { inList: filterState.qhp_teams_uuids } };
        }
        if (filterState.territories.length) {
            filters.territories = { id: { inList: filterState.territories } };
        }
        if (filterState.services.length) {
            filters.services = { id: { inList: filterState.services } };
        }
        if (filterState.patients.length) {
            filters.patient = { id: { inList: filterState.patients } };
        }
        if (filterState.patient_tag_ids.length) {
            filters.patient = {
                ...(filters.patient ?? {}),
                hasTag: { id: { inList: filterState.patient_tag_ids } },
            };
        }
        if (filterState.discipline_uuids.length) {
            filters.disciplines = { id: { inList: filterState.discipline_uuids } };
        }
        if (filterState.priority_codes.length) {
            filters.priority = { inList: filterState.priority_codes as VisitPriority[] };
        }
        if (filterState.orderState?.length) {
            filters.relatedOrder = orderStatesToOrderStateFilter(filterState.orderState);
        }

        return filters;
    }, [filterState]);
};

export const useNumVisitDialogFilters = (): number => {
    const filterState = useAtomValue(visitFiltersAtom);
    return useMemo(
        () => Object.values(filterState).filter((v) => Array.isArray(v) && v.length > 0).length,
        [filterState],
    );
};

export const useOnFilterChange = (callback: () => void) => {
    const filterState = useAtomValue(visitFiltersAtom);
    const lastFiltersRef = useRef(filterState);

    useEffect(() => {
        const hasChanged = Objects.entries(filterState).some(([filterName, filterValue]) => {
            const lastFilter = lastFiltersRef.current[filterName];
            return xor(filterValue ?? [], lastFilter ?? []).length > 0;
        });

        if (hasChanged) {
            callback();
            lastFiltersRef.current = filterState;
        }
    }, [filterState, callback]);
};
